import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { listBookingLogs, getBookingLogFilters, getBookingLogTimeline } from "../../services/bookingLogService";
import { TOKENS, Icon, ICONS, Pill, EmptyState, Spinner, inputClass, labelClass, btnSecondary, Pagination, DEFAULT_PAGE_SIZE } from "../../components/common/DashboardUI";

function errMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 19 ? s.slice(0, 19) : s;
};

// Short status transition, e.g. "Pending → Paid". Falls back to just the new
// status when there's no previous one (the row that first creates a booking).
function StatusTransition({ previous, next }) {
  if (!next) return <span className="text-gray-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      {previous && (
        <>
          <span className="text-gray-400">{previous}</span>
          <Icon path={ICONS.arrowRight} className="w-3 h-3 text-gray-300" width={2} />
        </>
      )}
      <span className="font-semibold" style={{ color: TOKENS.NAVY }}>{next}</span>
    </span>
  );
}

// ── Timeline modal — full audit history for one booking ─────────────────────
function TimelineModal({ bookingId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    setError("");
    getBookingLogTimeline(bookingId)
      .then((res) => on && setData(res))
      .catch((err) => on && setError(errMessage(err, "Could not load this booking's log timeline.")))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [bookingId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>

        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          Booking #{bookingId} — Log Timeline
        </h2>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <div className="space-y-5">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                ["Member", [data.booking.member_code, data.booking.member_name].filter(Boolean).join(" — ")],
                ["Facility", data.booking.facility_name],
                ["Total entries", data.booking.total_entries],
                ["Total fee", data.booking.total_fee != null ? money(data.booking.total_fee) : null],
                ["Booking status", data.booking.booking_status],
                ["Payment status", data.booking.payment_status],
                ["Payment reference", data.booking.payment_reference],
                ["Created by", data.booking.created_by_staff ? "Staff" : "Member (self-booking)"],
              ]
                .filter(([, v]) => v != null && v !== "")
                .map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-xs text-gray-400">{label}</dt>
                    <dd className="text-sm font-medium text-gray-700 truncate" style={{ color: TOKENS.NAVY }}>{value}</dd>
                  </div>
                ))}
            </dl>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
                Timeline ({data.logs.length})
              </h4>
              {data.logs.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No log entries recorded for this booking yet.</p>
              ) : (
                <ol className="relative border-l border-gray-100 pl-4 space-y-4">
                  {data.logs.map((log) => (
                    <li key={log.id}>
                      <span
                        className="absolute -left-[5px] w-2.5 h-2.5 rounded-full mt-1.5"
                        style={{ backgroundColor: TOKENS.PRIMARY }}
                      />
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <StatusTransition previous={log.previous_status} next={log.new_status} />
                        <span className="text-xs text-gray-400 font-mono whitespace-nowrap">{fmtDateTime(log.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-0.5">{log.summary}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {log.triggered_by_label}
                        {log.triggered_by_user_name ? ` — ${log.triggered_by_user_name}` : ""}
                      </p>
                      {log.payload && (
                        <pre className="text-xs bg-gray-50 rounded-lg p-3 mt-1.5 overflow-x-auto border border-gray-100" style={{ color: TOKENS.NAVY }}>
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <button type="button" onClick={onClose} className={btnSecondary}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function BookingLogs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: DEFAULT_PAGE_SIZE });

  const [search, setSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const searchTimer = useRef(null);
  const [triggeredBy, setTriggeredBy] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const [triggerOptions, setTriggerOptions] = useState([]);
  const [viewingBookingId, setViewingBookingId] = useState(null);

  useEffect(() => {
    getBookingLogFilters()
      .then((data) => setTriggerOptions(data?.triggered_by || []))
      .catch(() => setTriggerOptions([]));
  }, []);

  const loadLogs = useCallback(() => {
    setLoading(true);
    listBookingLogs({
      page,
      per_page: DEFAULT_PAGE_SIZE,
      search: search || undefined,
      triggered_by: triggeredBy || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    })
      .then((res) => {
        setRows(Array.isArray(res?.data) ? res.data : []);
        setMeta(res?.meta || { total: 0, page: 1, per_page: DEFAULT_PAGE_SIZE });
      })
      .catch((err) => toast.error(errMessage(err, "Could not load booking logs.")))
      .finally(() => setLoading(false));
  }, [page, search, triggeredBy, fromDate, toDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearchInput = (val) => {
    setDraftSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const lastPage = meta.per_page > 0 ? Math.max(1, Math.ceil(meta.total / meta.per_page)) : 1;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-bold text-xl" style={{ color: TOKENS.NAVY }}>Booking Logs</h1>
        <p className="text-sm text-gray-400 mt-1">
          Audit trail of every status change across all facility bookings — who/what triggered it and when.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <label className={labelClass}>Search</label>
            <div className="relative">
              <input
                className={`${inputClass} pr-9`}
                placeholder="Booking id, member name, payment reference…"
                value={draftSearch}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
              <Icon
                path={ICONS.search}
                className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                stroke="#9ca3af"
                width={2}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Triggered by</label>
            <select
              className={inputClass}
              value={triggeredBy}
              onChange={(e) => {
                setTriggeredBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              {triggerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>From</label>
              <input
                type="date"
                className={inputClass}
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <label className={labelClass}>To</label>
              <input
                type="date"
                className={inputClass}
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={ICONS.clock} text="No booking log entries match these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[900px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-3 px-4 font-medium">When</th>
                  <th className="py-3 px-4 font-medium">Booking</th>
                  <th className="py-3 px-4 font-medium">Member</th>
                  <th className="py-3 px-4 font-medium">Status Change</th>
                  <th className="py-3 px-4 font-medium">Triggered By</th>
                  <th className="py-3 px-4 font-medium">Summary</th>
                  <th className="py-3 px-4 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-gray-50 last:border-0 hover:bg-blue-50/40 cursor-pointer transition"
                    onClick={() => setViewingBookingId(row.booking_id)}
                  >
                    <td className="py-3 px-4 text-xs text-gray-500 font-mono whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                    <td className="py-3 px-4 text-xs font-semibold" style={{ color: TOKENS.NAVY }}>#{row.booking_id}</td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      <p className="font-medium" style={{ color: TOKENS.NAVY }}>{row.member_name || "—"}</p>
                      <p className="text-gray-400">{row.member_code}</p>
                    </td>
                    <td className="py-3 px-4">
                      <StatusTransition previous={row.previous_status} next={row.new_status} />
                    </td>
                    <td className="py-3 px-4 text-xs">
                      <Pill>{row.triggered_by_label}</Pill>
                      {row.triggered_by_user_name && <p className="text-gray-400 mt-1">{row.triggered_by_user_name}</p>}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600 max-w-xs truncate">{row.summary}</td>
                    <td className="py-3 px-4 text-xs font-semibold whitespace-nowrap" style={{ color: TOKENS.PRIMARY }}>
                      View Timeline
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta.total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Page {meta.page} of {lastPage} · {meta.total.toLocaleString()} entries
            </p>
            <Pagination page={meta.page} totalPages={lastPage} onPageChange={setPage} />
          </div>
        )}
      </div>

      {viewingBookingId != null && (
        <TimelineModal bookingId={viewingBookingId} onClose={() => setViewingBookingId(null)} />
      )}
    </div>
  );
}
